/**
 * The projects panel in a workspace's right rail.
 *
 * A project is a folder with its own member list and its own chat channel. The
 * thing that makes it more than a folder is the key: a project root's node key
 * is not derivable from the workspace root's, so being in the workspace does not
 * open a project you are not on — see `web/src/projects.ts` and migration 0007.
 *
 * That shows up here as the `locked` state. Projects a member is not assigned to
 * are **listed but unreadable**: no name, no unread count, no way in. Listing
 * them is deliberate — hiding them would make a shared workspace feel like it
 * had rooms nobody mentioned, and "ask to be added" is a conversation people can
 * have. Naming them is impossible, not withheld: the name is encrypted under the
 * key they do not hold.
 */

import { useCallback, useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { createProject, listProjects, renameProject, type DecryptedProject } from '../projects';
import { Footnote, Panel } from './Shell';
import { monoPlain } from '../theme';
import { useT } from '../i18n';

export function Projects({
  workspaceId,
  canCreate
}: {
  workspaceId: string;
  /** Workspace owner. Only they can start a project. */
  canCreate: boolean;
}) {
  const t = useT();
  const { Button, Icon, Input } = ds();
  const [projects, setProjects] = useState<DecryptedProject[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setProjects(await listProjects(workspaceId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load projects.');
    }
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const made = await createProject(workspaceId, name.trim());
      setProjects((prev) => [made, ...prev]);
      setName('');
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that project.');
    } finally {
      setBusy(false);
    }
  };

  const rename = async (p: DecryptedProject) => {
    const next = window.prompt(t('projects.rename-project'), p.name);
    if (!next || !next.trim() || next.trim() === p.name) return;
    setError(null);
    try {
      await renameProject(p, workspaceId, next.trim());
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rename that project.');
    }
  };

  return (
    <Panel style={{ padding: '18px 20px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="folder" size={16} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>
          {t('projects.projects')}
        </span>
        <span style={{ ...monoPlain, marginLeft: 'auto' }}>{projects.length}</span>
      </div>

      {projects.length === 0 && (
        <Footnote>{t('projects.no-projects-yet')}</Footnote>
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        {projects.map((p) =>
          p.locked ? (
            // No link: there is nothing behind it for this reader, and a row that
            // navigates to a 404 is worse than one that explains itself.
            <div
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 10,
                background: 'var(--surface-sunken)',
                color: 'var(--text-muted)'
              }}
            >
              <Icon name="lock" size={14} />
              <span style={{ fontSize: 13 }}>A project you are not on</span>
              <span style={{ ...monoPlain, marginLeft: 'auto' }}>{p.memberCount}</span>
            </div>
          ) : (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Link to={`/workspaces/${workspaceId}/projects/${p.id}`} style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 10,
                    cursor: 'pointer'
                  }}
                >
                  <Icon name="folder" size={14} />
                  <span
                    style={{
                      fontSize: 13,
                      color: 'var(--text-strong)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {p.name}
                  </span>
                  {p.unread > 0 && (
                    <span
                      style={{
                        background: 'var(--accent-600)',
                        color: '#fff',
                        borderRadius: 999,
                        fontSize: 11,
                        padding: '1px 7px'
                      }}
                    >
                      {p.unread > 99 ? '99+' : p.unread}
                    </span>
                  )}
                </span>
              </Link>
              {(p.role === 'lead' || canCreate) && (
                <Button variant="ghost" size="sm" onClick={() => void rename(p)}>
                  {t('projects.rename')}
                </Button>
              )}
              <span style={monoPlain}>{p.memberCount}</span>
            </div>
          )
        )}
      </div>

      {error && <Footnote>{error}</Footnote>}

      {canCreate &&
        (adding ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            style={{ display: 'grid', gap: 8 }}
          >
            <Input
              label={t('projects.project-name')}
              value={name}
              onChange={(e) => setName((e.target as HTMLInputElement).value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" variant="primary" size="sm" disabled={busy}>
                {busy ? 'Creating…' : 'Create'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
                {t('projects.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            {t('projects.new-project')}
          </Button>
        ))}
    </Panel>
  );
}
